/**
 * Derives a stable per-browser id from passive browser attributes, for use as
 * the Flipper actor id when bucketing visitors into a slow rollout.
 *
 * Only attributes the browser reports about itself are read. There is no
 * canvas, audio or font probing: those are the inputs browser vendors
 * randomize and regulators treat as fingerprinting, they are also the least
 * stable ones available, and Datadog RUM already collects everything used
 * here. The cost is coarse buckets, which is acceptable because the rollout
 * percentage is a dial rather than a measured cohort size.
 */

/**
 * Window key that forces a profile value, for Cypress and manual QA. Nothing
 * is persisted, so this is the only way to pin a bucket.
 */
export const DEVICE_PROFILE_OVERRIDE_KEY = '__VA_CHATBOT_DEVICE_PROFILE__';

/** Request header vets-api reads the profile from. */
export const DEVICE_PROFILE_HEADER = 'X-VA-Chatbot-Device-Profile';

// Order matters: an Edge user agent contains "Chrome/", and a Chrome one
// contains "Safari/", so the more specific tokens have to be tested first.
const BROWSER_FAMILIES = [
  ['Edg/', 'edge'],
  ['OPR/', 'opera'],
  ['Firefox/', 'firefox'],
  ['Chrome/', 'chrome'],
  ['Safari/', 'safari'],
];

/**
 * Browser family only. The full user agent carries a version that changes
 * every few weeks, which would re-bucket everyone on each browser release.
 *
 * @param {string} [userAgent]
 * @returns {string}
 */
export const getBrowserFamily = (userAgent = '') => {
  const match = BROWSER_FAMILIES.find(([token]) => userAgent.includes(token));
  return match ? match[1] : 'other';
};

/**
 * The attributes the profile is built from, labelled. Exported so a changed
 * profile can be diagnosed by naming which attribute moved.
 *
 * Deliberately excluded as unstable: the raw user agent (version churn), and
 * everything off `window.screen` -- dimensions, colorDepth and
 * devicePixelRatio all move with an external monitor, rotation, display
 * scaling or zoom. colorDepth is additionally normalized to 24 by most
 * browsers, so it carried that risk for no entropy.
 *
 * @returns {Record<string, string>}
 */
export const getDeviceProfileAttributes = () => {
  const nav = window.navigator || {};
  let timeZone = '';
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    timeZone = '';
  }

  const normalize = value =>
    value === undefined || value === null ? '' : String(value);

  return {
    browserFamily: getBrowserFamily(nav.userAgent),
    deviceMemory: normalize(nav.deviceMemory),
    hardwareConcurrency: normalize(nav.hardwareConcurrency),
    language: normalize(nav.language),
    maxTouchPoints: normalize(nav.maxTouchPoints),
    platform: normalize(nav.platform),
    timeZone,
  };
};

// A polynomial rolling hash, modulo a Mersenne prime to stay inside safe
// integer range. It does not need to be cryptographic: Flipper re-hashes the
// actor id with crc32 anyway, so this only has to be stable and opaque.
const MODULUS = 2147483647;

const hashString = input => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) % MODULUS;
  }
  return hash.toString(36);
};

/**
 * The profile value sent to vets-api as the Flipper actor id. Returns null if
 * it cannot be derived, so the caller resolves no features and the visitor
 * gets the unauthenticated experience.
 *
 * @returns {string|null}
 */
export const getDeviceProfile = () => {
  const override = window[DEVICE_PROFILE_OVERRIDE_KEY];
  if (typeof override === 'string' && override) {
    return override;
  }

  try {
    const attributes = getDeviceProfileAttributes();
    // Sorted so the serialized order never depends on key insertion order.
    const serialized = Object.keys(attributes)
      .sort()
      .map(key => `${key}=${attributes[key]}`)
      .join('|');
    return hashString(serialized);
  } catch (e) {
    return null;
  }
};

/**
 * Headers carrying the profile to vets-api. Empty unless the experience is
 * enabled, so nothing is derived or sent while the kill switch is off, and
 * empty again if the profile cannot be derived, which fails closed.
 *
 * @param {boolean} isEnabled
 * @returns {Record<string, string>}
 */
export const getDeviceProfileHeaders = isEnabled => {
  if (!isEnabled) {
    return {};
  }

  const profile = getDeviceProfile();
  return profile ? { [DEVICE_PROFILE_HEADER]: profile } : {};
};
